import React, { useEffect, useMemo, useState } from 'react';
import Footer from '../../components/Footer';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import {
  ApiProject,
  CATEGORY_LABELS,
  FEATURED_PROJECTS,
  getPlatformLinks,
  mergeFeaturedProjects,
  normalizeProject,
  Project,
  ProjectCategory,
  PROJECTS_API_URL,
} from './projectData';
import './Projects.scss';

type FilterCategory = 'all' | ProjectCategory;

interface CategoryTab {
  key: FilterCategory;
  label: string;
}

const CATEGORY_TABS: CategoryTab[] = [
  { key: 'all', label: '전체' },
  { key: 'own', label: '자체 플랫폼' },
  { key: 'outsource', label: '외주 프로젝트' },
];

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [category, setCategory] = useState<FilterCategory>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    const fetchProjects = async () => {
      setIsLoading(true);

      try {
        const response = await fetch(PROJECTS_API_URL, { signal: controller.signal });

        if (!response.ok) {
          throw new Error(`Projects API responded with ${response.status}`);
        }

        const data: ApiProject[] = await response.json();
        const normalized = Array.isArray(data) ? data.map(normalizeProject) : [];
        setProjects(mergeFeaturedProjects(normalized));
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('Failed to fetch projects:', error);
          setProjects(FEATURED_PROJECTS);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();

    return () => controller.abort();
  }, []);

  const filteredProjects = useMemo(() => {
    if (category === 'all') return projects;
    return projects.filter((project) => project.category === category);
  }, [projects, category]);

  const handleCategoryChange = (nextCategory: FilterCategory) => {
    if (nextCategory === category) return;

    setIsFiltering(true);
    setCategory(nextCategory);

    window.setTimeout(() => {
      setIsFiltering(false);
    }, 220);
  };

  const renderContent = () => {
    if (isLoading) {
      return <p className="projects-status">프로젝트를 불러오는 중입니다...</p>;
    }

    if (!projects.length) {
      return <p className="projects-status">등록된 프로젝트가 없습니다.</p>;
    }

    if (!filteredProjects.length) {
      return <p className="projects-status">해당 카테고리의 프로젝트가 없습니다.</p>;
    }

    return (
      <div className={`projects-grid ${isFiltering ? 'projects-grid--filtering' : ''}`}>
        {filteredProjects.map((project) => (
          <article
            key={project.id}
            className="project-card project-card--clickable"
            onClick={() => navigate(`/projects/${project.id}`)}
          >
            <Link
              to={`/projects/${project.id}`}
              className={`project-card__thumb project-card__thumb--${project.category}`}
              aria-label={`${project.title} 상세 보기`}
              onClick={(event) => event.stopPropagation()}
            >
              {project.thumbnailUrl ? (
                <img src={project.thumbnailUrl} alt={`${project.title} 썸네일`} />
              ) : (
                <div className="project-card__thumb-placeholder" aria-hidden="true" />
              )}
            </Link>

            <div className="project-card__body">
              <span className={`project-card__category project-card__category--${project.category}`}>
                {CATEGORY_LABELS[project.category]}
              </span>
              <h3 className="project-card__title">
                <Link to={`/projects/${project.id}`} onClick={(event) => event.stopPropagation()}>
                  {project.title}
                </Link>
              </h3>
              <p className="project-card__desc">{project.description}</p>

              <div className="project-card__tags">
                {project.techStack.map((tech) => (
                  <span key={`${project.id}-${tech}`} className="project-card__tag">
                    {tech}
                  </span>
                ))}
              </div>

              {getPlatformLinks(project).length ? (
                <div className="project-card__links">
                  {getPlatformLinks(project).map((link) => (
                    <a
                      key={link.key}
                      className={`project-card__link project-card__link--${link.key}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {link.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    );
  };

  return (
    <div className="projects">
      <Header />

      <section className="projects-hero">
        <div className="projects-hero__gradient" aria-hidden="true" />
        <div className="container projects-hero__inner">
          <span className="projects-hero__eyebrow">Portfolio</span>
          <h1 className="projects-hero__title">예플랫이 만들어온 디지털 혁신</h1>
          <p className="projects-hero__desc">
            기획부터 개발, 출시까지 성공적으로 이끌어낸 예플랫의 핵심 프로젝트를 소개합니다.
          </p>
        </div>
      </section>

      <section className="projects-section">
        <div className="container">
          <div className="projects-filter" role="tablist" aria-label="프로젝트 카테고리 필터">
            {CATEGORY_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={category === tab.key}
                className={`projects-filter__tab ${category === tab.key ? 'projects-filter__tab--active' : ''}`}
                onClick={() => handleCategoryChange(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Projects;
